/** @jsxRuntime classic */
/** @jsx jsx */
// eslint-disable-next-line no-restricted-imports
import { css, jsx } from "@emotion/core";
import * as React from "react";
import { Button, ButtonGroup, Dropdown, ExchangeIcon, Menu, MenuItem, PlusIcon, Theme } from "@ice/components";
import SingleColorChooser from "./SingleColorChooser";
import { MAX_CUSTOM_COLORS } from "./ColorPaletteChooser";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Fragment } from "react";
import {ColorUtils} from "../../util/ColorUtils";



/*
	Set of single color choosers - represents interactive color palette as
	a row of colors.
 */

interface ColorSetEditorProps {
	prevColors: string[];
	colors: string[];
	customPaletteIndex?: number;
	onColorsChange: (colors: string[]) => void;
	onConfirm: () => void;
	onCancel: () => void;
	savePalette: (index: number, colors: string[]) => void;
}

interface ColorSetEditorState {
	colors: string[];
	selectedIndex: number;
	customPaletteRef: number;
}

class ColorSetEditor extends React.Component<ColorSetEditorProps, ColorSetEditorState> {

	private chipDistance: number = 0;

	constructor(props: ColorSetEditorProps) {
		super(props);
		this.state = {
			colors: this.props.colors,
			selectedIndex: -1,
			customPaletteRef: this.props.customPaletteIndex
		};
	}

	private adaptColorPickerColors = () => {
		// no better way to customize color picker ???
		const el = document.querySelector(".sketch-picker");
		if (el) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			el.style.boxShadow = 'none';
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			el.style.background = 'none';
		}

		const spans = el?.querySelectorAll("span");
		if (spans) {
			// @ts-ignore
			for (const span of spans) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				span.style.color = '';
			}
		}
	};

	private calcChipDistance = () => {
		const el1 = document.getElementById("draggable_color_chip_0");
		const el2 = document.getElementById("draggable_color_chip_1");
		if (el1 && el2) {
			this.chipDistance = el2.offsetLeft - el1.offsetLeft;
		}
	}


	componentDidUpdate(prevProps: Readonly<ColorSetEditorProps>, prevState: Readonly<ColorSetEditorState>, snapshot?: any) {
		this.calcChipDistance();
	}

	componentDidMount() {
		this.calcChipDistance();
	}

	private colorUpdated = (colors: string[]) => {
		this.props.onColorsChange(colors);
	}

	private updateColor = (index: number, color: string) => {
		const colors = [...this.state.colors];
		colors[index] = color;
		this.setState({ colors: colors });
		this.colorUpdated(colors);
	};

	private addColor = () => {
		const colors = [...this.state.colors];
		colors.push(colors[colors.length - 1]);
		this.setState({ colors: colors, selectedIndex: colors.length - 1 });
		this.colorUpdated(colors);
	};

	private reverseColors = () => {
		const colors = [...this.state.colors].reverse();
		this.setState({ colors: colors });
		this.colorUpdated(colors);
	};

	private deleteColor = (index: number) => {
		const colors = [...this.state.colors];
		colors.splice(index, 1);
		this.setState({
			colors: colors,
			selectedIndex: -1,
		});
		this.colorUpdated(colors);
	};

	private reorder = (list: string[], startIndex, endIndex): string[] => {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);
		return result;
	};

	private onDragEnd = (result) => {
		// dropped outside the list
		if (!result.destination) {
			return;
		}

		if (result.source.index !== result.destination.index) {
			const newColors = this.reorder(this.state.colors, result.source.index, result.destination.index);
			this.setState({
				colors: newColors,
			});
			this.colorUpdated(newColors);
		}
	}

	private onCancel = () => {
		this.setState({ colors: this.props.prevColors});
		this.colorUpdated(this.props.prevColors);
		this.props.onCancel();
	}

	render() {
		const colorChips = [];
		for (let i = 0; i < this.state.colors.length; i++) {
			const c = this.state.colors[i];
			colorChips.push(
				<SingleColorChooser
					isEdited={this.state.selectedIndex === i}
					editMode={true}
					onColorChanged={color => this.updateColor(i, color)}
					onDelete={this.state.colors.length > 1 ? () => this.deleteColor(i) : null}
					color={c}
					onSelect={() => this.setState({ selectedIndex: i === this.state.selectedIndex ? -1 : i })}
					onEditFinished={() => this.setState({ selectedIndex: -1 })}
				/>
			);
		}

		const customPalettes = ColorUtils.getCustomPalettes();
		const customSaveItems = [];
		for (let i = 0; i < MAX_CUSTOM_COLORS; i++) {
			const defined = customPalettes.length > i && customPalettes[i];

			customSaveItems.push(
				<MenuItem
					css={(theme: Theme) => css`
						color: ${defined ? theme.focusAndAccentColor : theme.textColor}
					`}
					text={`Custom #${i + 1}`}
					onClick={() => {
						this.props.savePalette(i, this.state.colors);
						this.setState({customPaletteRef: i})
					}}
				/>
			);
		}

		return (
			<div style={{display: "flex", flexDirection: "column"}}>
				<div style={{ display: "flex", flexDirection: "row", alignItems: "center"}}>
					<DragDropContext key={`k_${this.state.colors}_${this.state.customPaletteRef}`} onDragEnd={this.onDragEnd}>
						<Droppable droppableId="droppable" direction="horizontal">
							{(provided, snapshot) => (
								<div
									ref={provided.innerRef}
									style={{display: "flex", flexDirection: "row"}}
									{...provided.droppableProps}
								>
									{colorChips.map((item, index) => (
										<Draggable key={`k_${index}`} draggableId={`k_${index}`} index={index}>
											{(provided, snapshot) => (
												<div
													id={`draggable_color_chip_${index}`}
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													style={{
														...provided.draggableProps.style,
														top: "auto !important",
														left: snapshot.isDragging ? `${this.chipDistance * index}px` : "auto !important",
													}}
												>
													{item}
												</div>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
					{
						this.state.colors.length < MAX_CUSTOM_COLORS &&
						<PlusIcon width="fixed" style={{cursor: "pointer"}} title={"Add new color"} onClick={() => this.addColor()} />
					}
					{
						this.state.colors.length > 1 &&
						<ExchangeIcon width="fixed" style={{cursor: "pointer"}} title={"Reverse colors"} onClick={() => this.reverseColors()} />
					}
				</div>
				<div style={{display: "flex", flexDirection: "row", alignItems: "center", marginTop: "0.4rem"}}>
					{
						this.state.customPaletteRef >= 0 ?
							<ButtonGroup>
								<Button onClick={() => {
									this.props.savePalette(this.state.customPaletteRef, this.state.colors);
								}} disabled={ColorUtils.isTheSameAsCustomPalette(this.state.customPaletteRef, this.state.colors)}>Save</Button>
								<Dropdown placement="bottom-start" renderMenu={getProps => (
									<Menu {...getProps()}>{customSaveItems}</Menu>
								)} square/>
							</ButtonGroup> :
							<Dropdown placement="bottom-start" renderMenu={getProps => (
								<Menu {...getProps()}>{customSaveItems}</Menu>
							)}>
								Save As
							</Dropdown>
					}
					{
						this.state.customPaletteRef >= 0 &&
						<Fragment>
							<div style={{ width: "0.4rem" }} />
							<div style={{fontSize: "0.9em", fontWeight: "bold", cursor: "default"}}>Custom #{this.state.customPaletteRef + 1}</div>
							<div style={{ width: "1rem" }} />
						</Fragment>
					}
					<div style={{flexGrow: 1}} />
					<Button intent={"primary"} onClick={() => this.props.onConfirm && this.props.onConfirm()}>
						OK
					</Button>
					<div style={{ width: "0.4rem" }} />
					<Button onClick={() => this.onCancel()}>Cancel</Button>
				</div>
			</div>
		);
	}
}


export default ColorSetEditor;
